import React, { useEffect, useMemo, useState } from "react";
import CareInstruction from "../../components/v2/CareInstruction";
import countries from "../../pages/v2/jsons/countries.json";
import { useTranslation } from "react-i18next";

const CareInstructions = ({
  care_instructions,
  productId,
  maintenance,
}) => {
  const { t, i18n } = useTranslation();

  const uiLang = (i18n.language || "en")
    .split("-")[0]
    .toLowerCase();

  const [careInstructionsInfo, setCareInstructionsInfo] = useState([]);
  const [loadingCareInstructions, setLoadingCareInstructions] =
    useState(true);

  /*
   * Exemplo do endpoint:
   *
   * {
   *   code: "WASHING_95_BOIL",
   *   description: [
   *     {
   *       language: "COUNTRY_GBR",
   *       value: "95 ºC Boiling Wash"
   *     },
   *     {
   *       language: "COUNTRY_PRT",
   *       value: "Lavagem em Ebulição a 95ºC"
   *     }
   *   ],
   *   img: "data:image/svg+xml;base64,...",
   *   careType: "WASHING"
   * }
   */
  useEffect(() => {
    const fetchCareInstructions = async () => {
      try {
        setLoadingCareInstructions(true);

        const response = await fetch("https://bio.infos.pt/api/defs/careInstruction");

        if (!response.ok) {
          throw new Error(
            `Erro ao obter care instructions: ${response.status}`
          );
        }

        const data = await response.json();

        console.log("RAW /defs/careInstruction:", data);

        let definitions = [];

        // Caso normal: endpoint devolve diretamente [...]
        if (Array.isArray(data)) {
          definitions = data;
        }

        // possíveis wrappers do backend
        else if (Array.isArray(data?.data)) {
          definitions = data.data;
        } else if (Array.isArray(data?.content)) {
          definitions = data.content;
        } else if (Array.isArray(data?.result)) {
          definitions = data.result;
        } else if (Array.isArray(data?.careInstruction)) {
          definitions = data.careInstruction;
        } else if (Array.isArray(data?.careInstructions)) {
          definitions = data.careInstructions;
        }

        // fallback: procura um array dentro do objeto
        else if (data && typeof data === "object") {
          const possibleArray = Object.values(data).find(
            (value) =>
              Array.isArray(value) &&
              value.some((item) => item?.code)
          );

          if (possibleArray) {
            definitions = possibleArray;
          }
        }

        console.log(
          "Care Instructions carregadas:",
          definitions.length
        );

        console.log(
          "Codes disponíveis:",
          definitions.map((item) => item?.code)
        );

        setCareInstructionsInfo(definitions);
      } catch (error) {
        console.error(
          "Erro ao carregar care instructions:",
          error
        );

        setCareInstructionsInfo([]);
      } finally {
        setLoadingCareInstructions(false);
      }
    };

    fetchCareInstructions();
  }, []);

  /*
   * COUNTRY_PRT -> pt
   * COUNTRY_GBR -> en
   *
   * usa countries.json mas adicionamos PT/EN explicitamente
   * para garantir que estes dois casos funcionam.
   */
  const countryCodeToLang = useMemo(() => {
    const map = {
      COUNTRY_PRT: "pt",
      COUNTRY_GBR: "en",
    };

    for (const country of countries || []) {
      if (country?.code && country?.lang) {
        map[country.code] = String(
          country.lang
        ).toLowerCase();
      }
    }

    return map;
  }, []);

  /*
   * Normaliza os codes para evitar problemas com espaços
   * ou diferenças de maiúsculas/minúsculas.
   */
  const normalizeCareCode = (value) =>
    String(value || "")
      .trim()
      .toUpperCase();

  /*
   * Transforma:
   *
   * [
   *   { code: "WASHING_30_MILD", ... },
   *   { code: "IRONING_DO_NOT_IRON", ... }
   * ]
   *
   * em:
   *
   * {
   *   WASHING_30_MILD: {...},
   *   IRONING_DO_NOT_IRON: {...}
   * }
   */
  const careInstructionByCode = useMemo(() => {
    const map = {};

    careInstructionsInfo.forEach((item) => {
      if (!item?.code) {
        return;
      }

      const normalizedCode = normalizeCareCode(item.code);

      map[normalizedCode] = item;
    });

    return map;
  }, [careInstructionsInfo]);

  /*
   * Escolhe a descrição no idioma atual.
   */
  const getDescriptionByUILang = (careInstruction) => {
    const descriptions = careInstruction?.description;

    if (!Array.isArray(descriptions)) {
      return null;
    }

    /*
     * 1. Idioma atual
     *
     * COUNTRY_PRT -> pt
     * COUNTRY_GBR -> en
     */
    const translated = descriptions.find((description) => {
      const language =
        countryCodeToLang[description?.language];

      return language === uiLang;
    });

    if (translated?.value) {
      return translated.value;
    }

    /*
     * 2. Fallback explícito PT
     */
    if (uiLang === "pt") {
      const portuguese = descriptions.find(
        (description) =>
          description?.language === "COUNTRY_PRT"
      );

      if (portuguese?.value) {
        return portuguese.value;
      }
    }

    /*
     * 3. Fallback inglês
     */
    const english = descriptions.find(
      (description) =>
        description?.language === "COUNTRY_GBR" ||
        countryCodeToLang[description?.language] === "en"
    );

    if (english?.value) {
      return english.value;
    }

    /*
     * 4. Último fallback
     */
    return descriptions[0]?.value || null;
  };

  /*
   * O produto atualmente devolve:
   *
   * care_instructions: [
   *   "WASHING_30_MILD",
   *   "BLEACHING_DO_NOT_BLEACH",
   *   "DRYING_DO_NOT_TUMBLE",
   *   "IRONING_DO_NOT_IRON",
   *   "PROFESSIONAL_DRY_CLEANING_TETRACHLOETHYLENE_NORMAL"
   * ]
   *
   * Portanto não precisamos de separar por categorias.
   */
  const careCodes = useMemo(() => {
    if (!Array.isArray(care_instructions)) {
      return [];
    }

    return care_instructions
      .filter(
        (code) =>
          typeof code === "string" &&
          code.trim() !== ""
      )
      .map(normalizeCareCode);
  }, [care_instructions]);

  /*
   * Faz o match entre o code do produto
   * e a definição vinda de /defs/careInstruction.
   */
  const getCareInstruction = (code) => {
    const normalizedCode = normalizeCareCode(code);

    const care =
      careInstructionByCode[normalizedCode];

    if (!care) {
      console.warn(
        `Care instruction não encontrada: ${normalizedCode}`
      );

      return null;
    }

    const description =
      getDescriptionByUILang(care);

    return {
      code: care.code,
      careType: care.careType,
      img: care.img,
      description:
        description ||
        t("unknownInstruction", {
          defaultValue: "Unknown Instruction",
        }),
    };
  };

  /*
   * Maintenance
   */
  const maintenanceHtml = useMemo(() => {
    if (!maintenance) {
      return "";
    }

    // já vem traduzido
    if (typeof maintenance === "string") {
      return maintenance;
    }

    if (Array.isArray(maintenance)) {
      /*
       * idioma atual
       */
      const translated = maintenance.find(
        (item) =>
          countryCodeToLang[item?.language] === uiLang
      );

      if (translated?.value) {
        return translated.value;
      }

      /*
       * fallback PT
       */
      if (uiLang === "pt") {
        const portuguese = maintenance.find(
          (item) =>
            item?.language === "COUNTRY_PRT"
        );

        if (portuguese?.value) {
          return portuguese.value;
        }
      }

      /*
       * fallback EN
       */
      const english = maintenance.find(
        (item) =>
          item?.language === "COUNTRY_GBR" ||
          countryCodeToLang[item?.language] === "en"
      );

      if (english?.value) {
        return english.value;
      }

      return maintenance[0]?.value || "";
    }

    return "";
  }, [
    maintenance,
    uiLang,
    countryCodeToLang,
  ]);

  /*
   * null / undefined
   */
  if (care_instructions == null) {
    return (
      <div
        className="tabela-container"
        style={{ alignItems: "center" }}
      >
        <div className="longtext-text centered-text">
          <p>
            {t(
              "care-instructions-are-not-available",
              {
                defaultValue:
                  "Care instructions are not available",
              }
            )}
            .
          </p>
        </div>

        {maintenanceHtml && (
          <div
            className="longtext-text"
            style={{ marginTop: "20px" }}
            dangerouslySetInnerHTML={{
              __html: maintenanceHtml,
            }}
          />
        )}
      </div>
    );
  }

  /*
   * array existe mas está vazio
   */
  if (
    Array.isArray(care_instructions) &&
    care_instructions.length === 0
  ) {
    return (
      <div
        className="tabela-container"
        style={{ alignItems: "center" }}
      >
        <div className="longtext-text centered-text">
          <p>
            {t(
              "care-instructions-are-not-available",
              {
                defaultValue:
                  "Care instructions are not available",
              }
            )}
            .
          </p>
        </div>

        {maintenanceHtml && (
          <div
            className="longtext-text"
            style={{ marginTop: "20px" }}
            dangerouslySetInnerHTML={{
              __html: maintenanceHtml,
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div>
      <div
        style={{ width: "100%" }}
        className="div-careinstructions"
      >
        <div
          style={{ width: "100%" }}
          className="div-careinstructions-content"
        >
          {loadingCareInstructions ? (
            <div className="longtext-text">
              {t("loading", {
                defaultValue: "Loading...",
              })}
            </div>
          ) : (
            careCodes.map((code) => {
              const care =
                getCareInstruction(code);

              /*
               * Importante:
               * se o code não existir nas defs,
               * não mostramos uma imagem partida
               * nem "UnknownInstruction".
               */
              if (!care) {
                return null;
              }

              return (
                <CareInstruction
                  key={care.code}
                  careinstruction={care}
                />
              );
            })
          )}
        </div>

        {maintenanceHtml && (
          <div
            className="longtext-text"
            style={{ marginTop: "20px" }}
            dangerouslySetInnerHTML={{
              __html: maintenanceHtml,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CareInstructions;